/**
 * @file scheduling-common.schema.ts
 * @description Tipos compartilhados pelo agendamento (Calendly).
 *
 * Vivem num arquivo próprio porque são usados por três schemas que não devem
 * se importar entre si: `partner.schema.ts` (defaults do parceiro),
 * `calendly-event-type-mapping.schema.ts` (overrides por agenda) e
 * `appointment.schema.ts` (snapshot congelado no momento do agendamento).
 */

import { z } from 'zod';

import { ButtonActionSchema } from './button-action.schema';

/**
 * Identificador da Eva como tenant no agendamento.
 *
 * A Eva não tem documento em `partners` — a configuração dela vive no
 * singleton `globalAppConfig` (id `default`). Reusar esse mesmo id como
 * "partnerId" da Eva mantém todas as queries, índices e coleções de
 * agendamento funcionando sem caso especial: a conexão dela é
 * `calendlyConnections/default`, os mapeamentos têm `partnerId: 'default'`,
 * e os agendamentos também.
 */
export const EVA_TENANT_ID = 'default';

/**
 * Card da seção "Como se preparar para a consulta" da tela de confirmação.
 *
 * É o mesmo par ícone + ação do CTA_CARD do page builder, o que deixa o admin
 * reaproveitar `IconPickerField`/`ButtonActionField` e o mobile reaproveitar
 * `TABLER_ICONS`/`useButtonAction`, sem componente novo dos dois lados.
 *
 * `icon` é uma key do registry TABLER_ICONS.
 */
export const PreparationCardSchema = z.object({
  id: z.string(),
  icon: z.string(),
  title: z.string(),
  /** O chevron do card leva a algum lugar — checkpoint, formulário, exames. */
  action: ButtonActionSchema,
  order: z.number().int().min(0),
});
export type PreparationCard = z.infer<typeof PreparationCardSchema>;

/**
 * Tipos de local suportados pelo Calendly (campo `location.kind` do
 * POST /invitees). Os que exigem input da convidada — `ask_invitee`,
 * `outbound_call` — não são suportados pelo fluxo nativo da v1: a tela não
 * coleta esse dado. Valide isso ao salvar o mapeamento, não no agendamento.
 */
export const CalendlyLocationKindEnum = z.enum([
  'physical',
  'inbound_call',
  'outbound_call',
  'custom',
  'google_conference',
  'zoom_conference',
  'microsoft_teams_conference',
  'gotomeeting',
  'webex_conference',
  'ask_invitee',
]);
export type CalendlyLocationKind = z.infer<typeof CalendlyLocationKindEnum>;

/** Kinds que geram um link de chamada (join_url) e não exigem input da convidada. */
export const CALENDLY_CONFERENCE_KINDS: readonly CalendlyLocationKind[] = [
  'google_conference',
  'zoom_conference',
  'microsoft_teams_conference',
  'gotomeeting',
  'webex_conference',
] as const;

/** Kinds que exigem input da convidada — não suportados pelo booking nativo da v1. */
export const CALENDLY_KINDS_REQUIRING_INVITEE_INPUT: readonly CalendlyLocationKind[] = [
  'ask_invitee',
  'outbound_call',
] as const;

/**
 * Configuração de agendamento no doc do parceiro.
 *
 * Contém APENAS dados não-sensíveis: tokens e signing key do webhook vivem na
 * coleção `calendlyConnections` (ver calendly-connection.schema.ts para o
 * porquê). Mesmo que este objeto vaze pelo endpoint público de branding, não há
 * dano.
 */
export const PartnerSchedulingConfigSchema = z.object({
  timezone: z.string().default('America/Sao_Paulo'),
  /** Cards de preparo usados quando o mapeamento não define os próprios. */
  defaultPreparationCards: z.array(PreparationCardSchema).default([]),
  /** Minutos antes da consulta em que os lembretes disparam. Padrão: 24h e 2h. */
  defaultReminderOffsetsMinutes: z.array(z.number().int().positive()).default([1440, 120]),
  /** Antecedência mínima para agendar (evita "consulta daqui a 5 minutos"). */
  defaultMinAdvanceMinutes: z.number().int().min(0).default(120),
  /** Teto da janela de disponibilidade. O Calendly não aceita range > 31 dias. */
  defaultMaxDaysAhead: z.number().int().min(1).max(31).default(31),
  cancellationPolicyText: z.string().default(''),
});
export type PartnerSchedulingConfig = z.infer<typeof PartnerSchedulingConfigSchema>;
